import React, { createContext, useContext, useEffect, useState } from 'react';
import packageJson from '../../package.json';

/**
 * VersionContext — Rastreia versão local vs servidor
 *
 * Faz polling a cada 5 minutos para detectar atualizações.
 * Dispara evento customizado quando versão mudou.
 *
 * SEGURANÇA:
 * - Falhas de polling são silenciosas (não quebram a app)
 * - Polling funciona mesmo sem autenticação
 * - Versão local vem de package.json (imutável no build)
 */

const VersionContext = createContext();

const APP_VERSION = packageJson.version;
const POLLING_INTERVAL_MS = 5 * 60 * 1000; // 5 minutos

/**
 * VersionProvider — Wrapper que fornece context
 */
export function VersionProvider({ children }) {
  const [localVersion, setLocalVersion] = useState(APP_VERSION);
  const [serverVersion, setServerVersion] = useState(null);
  const [isUpdateAvailable, setIsUpdateAvailable] = useState(false);
  const [lastChecked, setLastChecked] = useState(null);
  const [checkError, setCheckError] = useState(null);

  // Função de polling — silenciosa em caso de erro
  const checkVersion = async () => {

    try {
      // Fonte de verdade = version.json gerado no BUILD DO FRONTEND (mesmo artefato
      // que o bundle). Comparar com o backend (/api/version) era frágil: se backend
      // e frontend ficassem em versões diferentes (deploy não-atômico), o mismatch
      // nunca resolvia e o gate de atualização ficava preso. Com o version.json do
      // próprio frontend, depois que o usuário recarrega para o bundle novo os dois
      // valores batem e o gate some.
      //
      // 'no-store' é obrigatório: um version.json cacheado reintroduziria o mesmo
      // bug (versão velha eternizada). Reforçado por headers no nginx.
      const response = await fetch('/version.json', {
        method: 'GET',
        cache: 'no-store',
        headers: { 'Accept': 'application/json', 'Cache-Control': 'no-cache' },
        credentials: 'omit'
      });

      if (!response.ok) {
        // Em dev (npm start sem mock) o version.json não existe → 404: apenas
        // registra e não mostra gate. Em produção o arquivo é servido pelo nginx.
        setCheckError(`HTTP ${response.status}`);
        return;
      }

      const data = await response.json();
      const newVersion = data.version;

      setServerVersion(newVersion);
      setCheckError(null);

      // Detecta mudança de versão (normaliza para comparação)
      const normalizeVersion = (v) => String(v || '').replace(/^v/, '').toLowerCase();
      const normalizedNew = normalizeVersion(newVersion);
      const normalizedLocal = normalizeVersion(localVersion);

      if (normalizedNew !== normalizedLocal) {
        setIsUpdateAvailable(true);

        console.info(`[VersionContext] Update detected: ${localVersion} → ${newVersion}`);

        // Dispara evento customizado pra UpdateNotification
        window.dispatchEvent(
          new CustomEvent('versionMismatch', {
            detail: { newVersion, oldVersion: localVersion }
          })
        );
      }

      setLastChecked(new Date());
    } catch (err) {
      console.warn('[VersionContext] Version check failed:', err.message);
      setCheckError(err.message);
    }
  };

  // Polling ao montar + intervalo
  useEffect(() => {
    // Check imediato ao montar
    checkVersion();

    // Polling periódico
    const interval = setInterval(checkVersion, POLLING_INTERVAL_MS);

    // Cleanup
    return () => clearInterval(interval);
  }, []);

  // Expose para usar em componentes
  const value = {
    localVersion,
    serverVersion,
    isUpdateAvailable,
    lastChecked,
    checkError,
    // Força check imediato (pra testes ou botão manual)
    checkNow: checkVersion
  };

  return (
    <VersionContext.Provider value={value}>
      {children}
    </VersionContext.Provider>
  );
}

/**
 * useVersion — Hook pra acessar contexto
 */
export function useVersion() {
  const ctx = useContext(VersionContext);
  if (!ctx) {
    throw new Error('useVersion deve estar dentro de <VersionProvider>');
  }
  return ctx;
}
